import { useQuery } from '@apollo/client';
import { Nav } from '@teamsnap/teamsnap-ui';
import {
  hasPermissionForOrg,
  hasPermissionForProgram,
  Permission,
} from 'core/permissions';
import { addToArrayWhen } from 'core/utils';
import { AuthService, FeatureFlagService, Guards } from 'frontend-toolkit';
import * as React from 'react';
import { Icon } from '@teamsnap/snap-ui';
import { generatePath, Link, useHistory, useParams } from 'react-router-dom';
import {
  APP_URL,
  FUSION_APP_URL,
  TEAMSNAP_URL,
  TOURNAMENTS_ADMIN_URL,
} from '../core/constants';
import { ROUTES } from '../core/routes';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import * as UserNavigation from '../core/gql/queries/user-navigation';
import * as ShowProgram from '../core/gql/queries/show-program';
import * as ShowSeason from '../core/gql/queries/show-season';
import * as UserQuery from '../core/gql/queries/show-user';
import * as GqlOrganization from '../core/gql/queries/show-organizations';
import ENVIRONMENT from '../core/environment';

import './hub-nav.scss';

const buildNavItem = (
  name: string,
  iconName: string,
  isActive: boolean,
  link: string
) => (
  <Nav.Item
    key={name}
    isActive={isActive}
    wrapItem={({ children }) =>
      link.startsWith('/') ? (
        <Link to={link}>{children}</Link>
      ) : (
        <a href={link}> {children}</a>
      )
    }
  >
    <span className="Icon">
      <Icon name={iconName} filled size="s" />
    </span>{' '}
    <span className="Nav-itemName">{name}</span>
  </Nav.Item>
);

const TeamsnapApps = ({ items }) => (
  <div id="tsApps">
    <div style={{ border: '1px solid #162A42' }}></div>
    <span className="tsApps-title">TEAMSNAP APPS</span>
    {items}
  </div>
);

const WhatsNewLaunchNotes = () => (
  <Nav.Item>
    <span id="whatsnew-notifications-launch-notes">
      <span className="Icon">
        <Icon name="star_ellipse" filled size="s" />
      </span>{' '}
      <span className="Nav-itemName">What's New?</span>
    </span>
  </Nav.Item>
);

const HubNav = ({ hideOnMobile = false }: { hideOnMobile?: boolean }) => {
  const params = useParams<any>();
  const history = useHistory();
  const { orgId, programId, seasonId } = params;
  const userQuery = useQuery(UserQuery.QUERY);
  const currentUser = userQuery.data?.user;
  const isAdminOrGI = AuthService.checkGuard(
    Guards.AdminOrGhostInspector,
    currentUser
  );

  const getSeasonData = useQuery(ShowSeason.QUERY, {
    variables: { seasonId: +seasonId },
    skip: !seasonId,
    errorPolicy: 'all',
  });

  const getProgramData = useQuery(ShowProgram.QUERY, {
    variables: { programId: +programId },
    skip: !programId,
    errorPolicy: 'all',
  });

  const programName = getProgramData.data?.showProgram?.name;
  const seasonName = getSeasonData.data?.showSeason?.name;
  const divisionParam = getSeasonData.data?.showSeason?.tsDivisionId;
  const tsDivisionHasTeams =
    getSeasonData.data?.showSeason?.teams?.length &&
    getSeasonData.data?.showSeason?.teams?.some(
      (team) => team.divisionId == divisionParam
    );
  const splitChars = history.location.pathname.split('/');

  const lastWordFromUrl = splitChars[splitChars.length - 1];
  const activeNamespace = !isNaN(parseFloat(lastWordFromUrl))
    ? splitChars[splitChars.length - 2]
    : lastWordFromUrl;

  const rootPath = history.location.pathname.split('/')?.[1];

  const getNavData = useQuery(UserNavigation.QUERY, { errorPolicy: 'all' });

  const organization = getNavData.data?.userNavigation?.organizations?.find(
    (org) => +org.id === +orgId
  );

  const queryOrg = useQuery(GqlOrganization.QUERY, {
    variables: { organizationId: +orgId },
    skip: !orgId,
    errorPolicy: 'all',
  })?.data?.showOrganization;

  const organizationName = organization?.name;
  const tournamentOrgId = organization?.tournamentOrgId;

  const websiteUrl = organization?.websiteUrl
    ? organization?.websiteUrl.endsWith('/')
      ? `${organization?.websiteUrl}wp-admin`
      : `${organization?.websiteUrl}/wp-admin`
    : null;

  if (
    orgId == null &&
    rootPath !== 'employee' &&
    getNavData.data?.userNavigation?.organizations?.[0]
  ) {
    history.push(
      `/organizations/${getNavData.data?.userNavigation?.organizations?.[0].id}`
    );
  }

  const isOrgInvoicingEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.ORG_INVOICING],
    ENVIRONMENT,
    +orgId
  );

  const orgItems = addToArrayWhen(
    [
      [
        buildNavItem(
          'Programs',
          'division',
          activeNamespace === 'programs',
          `/organizations/${orgId}/programs`
        ),
        hasPermissionForOrg(currentUser, Permission.ProgramsView, orgId),
      ],
      [
        buildNavItem(
          'Financials',
          'account_balance',
          splitChars.includes('financials'),
          orgId ? generatePath(ROUTES.FINANCIALS, { orgId }) : ''
        ),
        orgId &&
          hasPermissionForOrg(currentUser, Permission.OrgFinancialView, orgId),
      ],
      [
        buildNavItem(
          'Calendar',
          'calendar_month',
          activeNamespace === 'calendar',
          `/organizations/${orgId}/calendar`
        ),
        orgId &&
          hasPermissionForOrg(currentUser, Permission.OrgCalendarView, orgId),
      ],
      [
        buildNavItem(
          'Members',
          'person',
          activeNamespace === 'members',
          `/organizations/${orgId}/members`
        ),
        hasPermissionForOrg(currentUser, Permission.MembersView, orgId),
      ],
      [
        buildNavItem(
          'Staff',
          'star',
          activeNamespace === 'staff',
          `/organizations/${orgId}/staff`
        ),
        hasPermissionForOrg(currentUser, Permission.StaffView, orgId),
      ],
      [
        buildNavItem(
          'Registration',
          'description',
          splitChars.includes('registration'),
          orgId ? generatePath(ROUTES.REGISTRATION_INDEX, { orgId }) : ''
        ),
        (hasPermissionForOrg(currentUser, Permission.OrgView, orgId) ||
          isAdminOrGI) &&
          orgId,
      ],
      [
        buildNavItem(
          'Invoices',
          'credit_card',
          activeNamespace === 'invoices',
          `/organizations/${orgId}/invoices`
        ),
        isOrgInvoicingEnabled &&
          hasPermissionForOrg(currentUser, Permission.OrgView, orgId),
      ],
      [
        buildNavItem(
          'Messages',
          'message',
          activeNamespace === 'messages',
          `/organizations/${orgId}/messages`
        ),
        hasPermissionForOrg(currentUser, Permission.MessagesView, orgId),
      ],
      [
        buildNavItem(
          'Settings',
          'settings',
          activeNamespace === 'settings',
          `/organizations/${orgId}/settings`
        ),
        hasPermissionForOrg(currentUser, Permission.OrgUpdate, orgId),
      ],
      [
        buildNavItem(
          'Divisions',
          'division',
          activeNamespace === 'divisions',
          `/organizations/${orgId}/divisions`
        ),
        hasPermissionForOrg(
          currentUser,
          Permission.CoordinatedDivisionsView,
          orgId
        ),
      ],
    ],
    []
  );

  const seasonItems = addToArrayWhen(
    [
      [
        buildNavItem(
          tsDivisionHasTeams ? 'Teams' : 'Divisions',
          'division',
          activeNamespace === 'divisions',
          `${TEAMSNAP_URL}/${divisionParam}/division_teams`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.SeasonDivisionsView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Financials',
          'account_balance',
          activeNamespace === 'financials',
          `/organizations/${orgId}/programs/${programId}/seasons/${seasonId}/financials`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.SeasonFinancialsView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Invoicing',
          'credit_card',
          activeNamespace === 'invoicing',
          `${TEAMSNAP_URL}/${divisionParam}/league_payments/invoicing`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.SeasonInvoicingView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Registration',
          'description',
          activeNamespace === 'registration',
          queryOrg?.registrationType === 'reg1'
            ? `${TEAMSNAP_URL}/divisions/${divisionParam}/registration`
            : `/organizations/${orgId}/programs/${programId}/seasons/${seasonId}/registration`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.SeasonRegistrationView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Rostering',
          'arrow_selector_tool',
          activeNamespace === 'rostering',
          `${TEAMSNAP_URL}/${divisionParam}/league_rostering`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.MembersView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Participants',
          'person',
          activeNamespace === 'participants',
          `/organizations/${orgId}/programs/${programId}/seasons/${seasonId}/participants`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.MembersView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Members',
          'person',
          activeNamespace === 'members',
          `${TEAMSNAP_URL}/${divisionParam}/league_members/list`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.MembersView,
          orgId,
          programId
        ) && [5911, 6805, 24703, 27060, 27706, 37100].includes(+orgId),
      ],
      [
        buildNavItem(
          'Schedule',
          'calendar_month',
          activeNamespace === 'schedule',
          `${TEAMSNAP_URL}/${divisionParam}/league_schedule`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.SeasonScheduleView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Messages',
          'message',
          activeNamespace === 'messages',
          `${TEAMSNAP_URL}/${divisionParam}/league_messages`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.SeasonMessagesView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Safety',
          'verified_user',
          activeNamespace === 'safety',
          `${TEAMSNAP_URL}/${divisionParam}/safety`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.SeasonSafetyView,
          orgId,
          programId
        ),
      ],
      [
        buildNavItem(
          'Settings',
          'settings',
          activeNamespace === 'settings',
          `${TEAMSNAP_URL}/${divisionParam}/league_settings`
        ),
        hasPermissionForProgram(
          currentUser,
          Permission.SeasonSettingsView,
          orgId,
          programId
        ),
      ],
    ],
    []
  );

  const tournamentsUrl = tournamentOrgId
    ? `${TOURNAMENTS_ADMIN_URL}/organization/${tournamentOrgId}`
    : `/organizations/${orgId}/tournaments`;

  const teamsnapAppsItems = [
    buildNavItem(
      'Websites',
      '',
      activeNamespace === 'websites',
      websiteUrl ?? `/organizations/${orgId}/websites`
    ),
    buildNavItem(
      'Coaches Pack',
      '',
      activeNamespace === 'coaches-pack',
      `/organizations/${orgId}/coaches-pack`
    ),
    buildNavItem(
      'Tournaments',
      '',
      activeNamespace === 'tournaments',
      tournamentsUrl
    ),
    buildNavItem(
      'Sponsorships',
      '',
      activeNamespace === 'sponsorships',
      `/organizations/${orgId}/sponsorships`
    ),
  ];

  const orgTree = React.useMemo(() => {
    if (!getNavData.data?.userNavigation?.organizations) {
      return [];
    }
    return getNavData.data.userNavigation?.organizations?.map((org) => ({
      title: org.name,
      // useBadge: true,
      tree: [
        {
          title: 'Organization Home',
          wrapItem: ({ children }) => {
            if (org.isOrgV2) {
              return (
                <a
                  href={`${FUSION_APP_URL}/organizations/${org.id}/programs`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {children}
                </a>
              );
            }
            const destination = `/organizations/${org.id}/programs`;
            return (
              <Link
                className={`Nav-child ${
                  destination === history.location.pathname
                    ? 'Nav-selected'
                    : ''
                }`}
                to={`/organizations/${org.id}`}
              >
                {children}
              </Link>
            );
          },
        },
        ...org.programs?.map((program) => ({
          title: program.name,
          useBadge: true,
          wrapItem: ({ children }) => {
            const destination = `/organizations/${org.id}/programs/${program.id}`;
            return (
              <span
                data-destination={destination}
                className={`${
                  destination === history.location.pathname
                    ? 'Nav-selected'
                    : ''
                }`}
              >
                {children}
              </span>
            );
          },
          tree:
            !program?.seasons || program?.seasons.length === 0
              ? null
              : program?.seasons.flatMap((season) =>
                  !season.coordinatedDivisions ||
                  season.coordinatedDivisions.length === 0
                    ? {
                        title: season.name,
                        wrapItem: ({ children }) => (
                          <a
                            href={`${TEAMSNAP_URL}/team/set_division/${season.tsDivisionId}`}
                          >
                            {children}
                          </a>
                        ),
                      }
                    : season.coordinatedDivisions?.map((division) => ({
                        title: division.name,
                        wrapItem: ({ children }) => (
                          <a
                            href={`${TEAMSNAP_URL}/team/set_division/${division.id}`}
                          >
                            {children}
                          </a>
                        ),
                      }))
                ),
        })),
      ],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNavData]);

  return (
    <>
      <Nav
        mods={`u-size2of12 ${hideOnMobile ? 'hideOnMobile' : ''}`}
        headerItem={{
          image:
            'https://storage.googleapis.com/ts_assets_prod-classic_assets/logo-icon-dafd29abff7b6ca55ad71c35bd34d679.png',
          title: seasonId ? programName : organizationName,
          subtitle: seasonId ? seasonName : null,
        }}
        flyoutSections={[
          {
            tree: orgTree,
          },
        ]}
        includeOverlay
        openItems
        footerImage={`${APP_URL}/images/TS_For_Business_White.png`}
      >
        {seasonId ? (
          seasonItems
        ) : (
          <>
            {orgItems}
            <WhatsNewLaunchNotes />
            <TeamsnapApps items={teamsnapAppsItems} />
          </>
        )}
      </Nav>
    </>
  );
};

export default HubNav;
